import { GridRowSelectionModel, GridSortModel } from '@mui/x-data-grid-pro'
import TextInput from 'ext-ui/components/TextInput/TextInput'
import React, { useEffect, useMemo, useState } from 'react'
import { CardholderSelection } from 'src/app/CardFeedDetails/components/AssignCardholderDropdown'
import * as style from 'src/app/CardFeedDetails/components/CardFeedCardsTable.style'
import { buildCardFeedColumns } from 'src/app/CardFeedDetails/components/cardFeedColumns'
import CardSelectionSnackbar from 'src/app/CardFeedDetails/components/CardSelectionSnackbar'
import CardStatusFilter from 'src/app/CardFeedDetails/components/CardStatusFilter'
import { resolveSelectedCards } from 'src/app/CardFeedDetails/utils/resolveSelectedCards'
import {
  CardFeedCardsView,
  CardFeedSortField,
  selectVisibleCards,
} from 'src/app/CardFeedDetails/utils/selectVisibleCards'
import Box from 'src/components/Box/Box'
import DataGrid, { DataGridRowHeight } from 'src/components/DataGrid/DataGrid'
import { useTranslate } from 'src/localization/Trns'
import {
  CardFeedCard,
  CardFeedCardStatus,
} from 'src/queries/cardFeeds/cardFeeds.models'
import useQueryCardFeedCards from 'src/queries/cardFeeds/domain/useQueryCardFeedCards'

export interface CardFeedCardsTableProps {
  feedId: string
  /**
   * Called with the cards the user wants to assign an account owner to —
   * fires when the user clicks the snackbar's primary action with one or more
   * unassigned cards selected. Required because the snackbar's primary action
   * is part of the table's bulk-action contract; a hypothetical read-only
   * caller would need to also suppress the snackbar, not just omit the
   * callback.
   */
  onAssignRequest: (cards: CardFeedCard[]) => void
  /**
   * Fires when the user picks a cardholder from a single row's inline
   * dropdown — bypasses the bulk flyout and goes straight to the parent's
   * confirmation modal with both the card and the selected recipient.
   */
  onAssignFromRow: (card: CardFeedCard, selection: CardholderSelection) => void
  /**
   * Incremented by the parent after a successful assignment to clear the
   * current selection — the assigned cards transition out of UNASSIGNED, so
   * the bulk selection no longer applies.
   */
  resetSelectionSignal?: number
}

const GRID_HEIGHT = 560
const SORTABLE_FIELDS: CardFeedSortField[] = [
  'cardName',
  'cardHolder',
  'status',
  'dateAdded',
]

// MUI X 8 models row selection as `{ type, ids }`, not a plain id array. A
// factory (not a shared constant) guarantees every init/reset gets a fresh
// model with its own Set, so no instance or reset can mutate another's state.
const createEmptySelection = (): GridRowSelectionModel => ({
  type: 'include',
  ids: new Set(),
})

const CardFeedCardsTable: React.FC<CardFeedCardsTableProps> = ({
  feedId,
  onAssignRequest,
  onAssignFromRow,
  resetSelectionSignal,
}) => {
  const translate = useTranslate()
  const { cards, isTruncated, isLoading, isError } = useQueryCardFeedCards(feedId)

  const [searchValue, setSearchValue] = useState('')
  const [statuses, setStatuses] = useState<CardFeedCardStatus[]>([])
  const [sortModel, setSortModel] = useState<GridSortModel>([])
  const [selectionModel, setSelectionModel] =
    useState<GridRowSelectionModel>(createEmptySelection)

  useEffect(() => setSelectionModel(createEmptySelection()), [feedId])
  useEffect(() => {
    if (resetSelectionSignal === undefined) return
    setSelectionModel(createEmptySelection())
  }, [resetSelectionSignal])

  const view: CardFeedCardsView = useMemo(() => {
    const sort = sortModel[0]
    const sortField =
      sort && SORTABLE_FIELDS.includes(sort.field as CardFeedSortField)
        ? (sort.field as CardFeedSortField)
        : undefined
    return {
      search: searchValue,
      statuses,
      sortField,
      sortDirection: sort?.sort === 'desc' ? 'DESC' : 'ASC',
    }
  }, [searchValue, statuses, sortModel])

  const visibleCards = useMemo(
    () => selectVisibleCards(cards, view),
    [cards, view],
  )

  const columns = useMemo(
    () => buildCardFeedColumns({ translate, onAssignFromRow }),
    [translate, onAssignFromRow],
  )

  const isFiltering = searchValue.trim().length > 0 || statuses.length > 0

  // Resolve against visibleCards (the rows in the grid), not the full feed, so
  // "select all" while a filter is active never pulls in cards the user can't
  // see. See resolveSelectedCards for the include/exclude model handling.
  const selectedCards = useMemo(
    () => resolveSelectedCards(visibleCards, selectionModel),
    [visibleCards, selectionModel],
  )

  const handleAssignSelected = (): void => {
    if (selectedCards.length === 0) return
    onAssignRequest(selectedCards)
  }

  const noRowsOverlay = (): React.ReactElement => (
    <div
      css={style.overlay}
      data-testid={
        isFiltering ? 'card-feed-cards-no-match' : 'card-feed-cards-empty'
      }
    >
      {isFiltering
        ? translate('No cards match your search or filters.')
        : translate('No cards yet — your bank will activate this feed shortly.')}
    </div>
  )

  return (
    <Box
      noPadding
      outlineBorder
      css={style.tableCard}
      data-testid="card-feed-cards-table"
    >
      <div css={style.toolbar}>
        <div css={style.search}>
          <TextInput
            value={searchValue}
            onChange={(e): void => setSearchValue(e.target.value)}
            placeholder={translate('Search...')}
            aria-label={translate('Search cards')}
            testId="card-feed-cards-search"
          />
        </div>
        <CardStatusFilter statuses={statuses} onChange={setStatuses} />
      </div>

      {isError ? (
        <div css={style.overlay}>
          <span role="alert" data-testid="card-feed-cards-error">
            {translate(
              "We couldn't load the cards for this feed. Please try again.",
            )}
          </span>
        </div>
      ) : isLoading ? (
        <div
          css={style.overlay}
          role="status"
          aria-live="polite"
          data-testid="card-feed-cards-loading"
        >
          {translate('Loading...')}
        </div>
      ) : (
        <>
          {isTruncated && (
            <div
              css={style.truncationNotice}
              data-testid="card-feed-cards-truncation"
            >
              {translate(
                'Showing first 100 cards. Sorting and filtering apply to these only.',
              )}
            </div>
          )}
          <div css={style.gridWrap}>
            <DataGrid<CardFeedCardsView, CardFeedCard>
              height={GRID_HEIGHT}
              rows={visibleCards}
              columns={columns}
              filtersBody={view}
              rowHeight={DataGridRowHeight.Default}
              sortModel={sortModel}
              onSortModelChange={setSortModel}
              checkboxSelection
              keepNonExistentRowsSelected
              isRowSelectable={(params): boolean =>
                params.row.status === 'UNASSIGNED'
              }
              rowSelectionModel={selectionModel}
              onRowSelectionModelChange={setSelectionModel}
              disableColumnFilter
              disableRowSelectionOnClick
              slots={{ noRowsOverlay }}
            />
          </div>
        </>
      )}

      <CardSelectionSnackbar
        selectedCount={selectedCards.length}
        onAssign={handleAssignSelected}
        onClose={(): void => setSelectionModel(createEmptySelection())}
      />
    </Box>
  )
}

export default CardFeedCardsTable
